import {
  AssignmentOutlined,
  CheckCircleOutline,
  HourglassBottom,
  NotificationsNone,
  TrendingUp,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import React, { FC } from 'react';

interface RfqStatusCardsProps {
  totalLeads: number;
  newLeads: number;
  inProgressLeads: number;
  convertedLeads: number;
}

interface StatCardData {
  key: string;
  label: string;
  value: number | string;
  subtitle: string;
  icon: React.ElementType;
  iconColor: string;
  iconBgColor: string;
}

const RfqStatusCards: FC<RfqStatusCardsProps> = ({
  totalLeads,
  newLeads,
  inProgressLeads,
  convertedLeads,
}) => {
  const total = totalLeads || 0;
  const converted = convertedLeads || 0;
  const conversionRate = (total ? (converted / total) * 100 : 0).toFixed(1);

  const stats: StatCardData[] = [
    {
      key: 'total',
      label: 'Total RFQs',
      value: total,
      subtitle: 'All requests',
      icon: AssignmentOutlined,
      iconColor: '#1976D2',
      iconBgColor: '#E3F2FD',
    },
    {
      key: 'new',
      label: 'New Requests',
      value: newLeads || 0,
      subtitle: 'Needs attention',
      icon: NotificationsNone,
      iconColor: '#B75B00',
      iconBgColor: '#FFF4E5',
    },
    {
      key: 'inProgress',
      label: 'In Progress',
      value: inProgressLeads || 0,
      subtitle: 'Being followed up',
      icon: HourglassBottom,
      iconColor: '#175CD3',
      iconBgColor: '#EAF2FF',
    },
    {
      key: 'converted',
      label: 'Converted',
      value: converted,
      subtitle: `${converted} this month`,
      icon: CheckCircleOutline,
      iconColor: '#16794A',
      iconBgColor: '#E8F7EE',
    },
    {
      key: 'conversion',
      label: 'Conversion Rate',
      value: `${conversionRate}%`,
      subtitle: `${converted} of ${total} converted`,
      icon: TrendingUp,
      iconColor: '#7B1FA2',
      iconBgColor: '#F3E5F5',
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(5, 1fr)',
        },
        gap: 1.25,
        width: '100%',
      }}>
      {stats.map(stat => {
        const IconComponent = stat.icon;
        return (
          <Box
            key={stat.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 1.5,
              py: 1.25,
              minHeight: 72,
              borderRadius: '10px',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E7EB',
            }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: stat.iconBgColor,
                color: stat.iconColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
              <IconComponent sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.15 }}>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                  lineHeight: 1.2,
                }}>
                {stat.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: '#101828',
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em',
                }}>
                {stat.value}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 400,
                  color: 'text.secondary',
                  lineHeight: 1.2,
                }}>
                {stat.subtitle}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default RfqStatusCards;
